// `mapExtensionCriteriaToTempCriteria` and the nine `init*Criteria` functions it calls
// (search.page.component.ng1.ts:662) — the alert's SAVED criteria, turned into the shape the
// sections edit.
//
// ⚠️ THE WIRE SHAPE AND THE FORM SHAPE ARE NOT THE SAME SHAPE, and that is the whole of this
// file. Five of the nine rename a field, three derive a flag that is not on the wire at all,
// and every one of them has an EMPTY-STATE DEFAULT that is not `{}`. Loading the criteria by
// assigning them straight onto the sections' state — which type-checks, because most of the
// names do line up — produces a form that looks populated and is wrong in ways that only show
// against production.
//
// The empty-state defaults are the half that bites even on an alert with no criteria at all,
// because that is the state this screen loads in:
//
//     field of expertise   [{ minYears: null, maxYears: null, when: RECENTLY }]  ← not `[{}]`
//     skill set            [{ skills: [], lastUsed: ANY_TIME, allowUnknownYears: false }]
//     keyword search       [{ sectionCategory: CURRENT_WORK_EXPERIENCE, conditionType: INCLUDE_ANY }]
//     industries/companies { period: ANY_TIME }
//     company types/tiers  { period: ANY_TIME }
//     quality companies    { period: ANY_TIME }
//     candidate turnover   { minTurnoverRate: 1, condition: EXCLUDE }
//     location             {}  — but `distance` becomes 10 the moment a location exists
//
// `initSkillsCriteria` and `initFieldOfExpertiseCriteria` also guard DIFFERENTLY on an empty
// array: FOE checks `length > 0` and seeds a row, skills checks only `!!criteria` and returns
// the empty array unchanged. So a saved alert with `skills: []` shows NO skill row where the
// same alert with `fieldOfExpertise: []` shows one.
import { ClientContactKeywordSectionCategory, CriteriaConditionType, KeywordSectionCategory, LastUsedPeriod } from './criteriaOptions.ts'
import type { FieldOfExpertiseRow } from './FieldOfExpertiseSection.tsx'
import type { SkillCriteriaRow } from './SkillSetSection.tsx'
import type { WorkExperienceCriteria } from './WorkExperienceSection.tsx'
import type { EducationCriteria, EducationGroup } from './EducationSection.tsx'
import type { ContactDetailsCriteria } from './ContactDetailsSection.tsx'
import type { LocationCriteria } from './LocationSection.tsx'
import type { KeywordCondition } from './KeywordSearchSection.tsx'
import type { AtsFilterRow } from './atsFilterData.ts'
import type { ExternalSourceFilterRow } from './externalSourceFilterData.ts'

/** `IExtensionCriteria` as the server sends it. Every key may be null. */
export interface SavedCriteria {
  fieldOfExpertise?: SavedFieldOfExpertise[] | null
  fieldOfExpertiseOperator?: number | null
  skills?: SavedSkillCriteria[] | null
  industries?: { industries?: Array<{ text: string }> | null; period?: number } | null
  companies?: { companies?: string[] | null; period?: number } | null
  companyTypes?: { startup?: boolean; corporate?: boolean; period?: number } | null
  companyTiers?: { period?: number } | null
  qualityCompanyOnly?: { period?: number } | null
  candidateTurnover?: { minTurnoverRate?: number; condition?: number | null } | null
  education?: SavedEducation | null
  location?: SavedLocation | null
  contactDetails?: { hasEmailOnly?: boolean; hasPhoneOnly?: boolean } | null
  keywords?: { conditions?: SavedKeywordCondition[] | null } | null
  atsFilters?: Array<{ section?: string; data?: unknown }> | null
  externalSourceSearchCriteriaFilters?: Record<string, ExternalSourceFilterRow[]> | null
  versionId?: number
}

export interface SavedFieldOfExpertise {
  field?: string
  position?: string
  otherPositions?: string[] | null
  exactTextMatch?: boolean
  /** ⚠️ `yoe`, not `minYears`/`maxYears` — the form splits it into two fields. */
  yoe?: { min?: number | null; max?: number | null } | null
  when?: number
}

export interface SavedSkillCriteria {
  /** Plain strings on the wire; the form's tag input wants `{ text }`. */
  skills?: string[] | null
  /** ⚠️ `yearsRange` here, `yoe` on field-of-expertise. Same idea, two names. */
  yearsRange?: { min?: number | null; max?: number | null } | null
  lastUsed?: number
  allowUnknownYears?: boolean
}

export interface SavedKeywordCondition {
  sectionCategory?: number
  conditionType?: number
  terms?: string[] | null
}

export interface SavedEducation {
  degreeType?: Record<string, boolean> | null
  fieldCategory?: Record<string, boolean> | null
  instituteBadge?: Record<string, boolean> | null
}

export interface SavedLocation {
  country?: string
  /** ⚠️ AN ARRAY OF OBJECTS keyed `region_state`, against the form's `Record<name, boolean>`. */
  regions?: Array<{ region_state: string }> | null
  cities?: Array<{ text?: string; city?: string }> | null
  globalCitiesOrRegions?: Array<{ text?: string; id?: string }> | null
  /** ⚠️ `distances` on the wire, `distanceCities` in the form — and it carries the radius. */
  distances?: Array<{ text?: string; city?: string; distance?: number }> | null
  allowUnknown?: boolean
}

/**
 * `getNewFOE()` — ⚠️ `when` is RECENTLY, not unset.
 *
 * ⚠️ THE SENTINEL IS GATED ON THE FEATURE FLAG, NOT ON KNOWING ITS NAME. Upstream is
 * `if (this.extendedTitleClassFeatureEnabled) newFOE.field = this.otherFieldsFieldName`, and
 * the two conditions were indistinguishable here for as long as the tenant served no
 * `otherFieldsFieldName` — absent name, no field, same result either way. The Track B deploy
 * on 7 Oct started serving it (`"OTHER"`), and the difference became reachable: a tenant that
 * serves the name with the flag OFF would get the sentinel from this port and not from
 * upstream, which counts an untouched Field-of-expertise section as a used filter.
 *
 * It does not bite on the dev tenant, where the flag is true — but a latent divergence that
 * only appears on someone else's tenant is the kind this port exists to avoid.
 */
export function emptyFieldOfExpertiseRow(
  otherFieldsFieldName?: string,
  extendedTitleClassEnabled?: boolean,
): FieldOfExpertiseRow {
  return {
    minYears: null,
    maxYears: null,
    when: LastUsedPeriod.Recently,
    ...(extendedTitleClassEnabled === true && otherFieldsFieldName !== undefined
      ? { field: otherFieldsFieldName }
      : {}),
  }
}

/**
 * `ExtensionCriteriaService.latestCandidateCriteriaVersionId` / `…ClientContactCriteriaVersionId`
 * (`extension-criteria.service.ng1.ts:29`).
 *
 * ⚠️ NOT COSMETIC, AND THE TWO PRODUCTS DO NOT SHARE A NUMBER. `getNewEmptyCriteria()` stamps a
 * brand-new criteria object with the latest version, and the service keeps a migration map
 * keyed on it (`0→1`, `1→2`, `2→3`) that it applies on the way IN. Criteria saved with no
 * version are therefore not "unversioned" — they are a candidate for a migration path that no
 * longer describes them.
 */
export const LATEST_CANDIDATE_CRITERIA_VERSION = 4
export const LATEST_CLIENT_CONTACT_CRITERIA_VERSION = 1

/** `getEmptyTempSkillCriteria()`. */
export function emptySkillRow(): SkillCriteriaRow {
  return { skills: [], minYears: null, maxYears: null, lastUsed: LastUsedPeriod.AnyTime, allowUnknownYears: false }
}

/**
 * `getEmptyTempKeywordCriteriaCondition()` — the CANDIDATE one, whose default category is the
 * enum's LAST member. See `emptyClientContactKeywordCondition` below for the contact form's,
 * which is a different number.
 */
export function emptyKeywordCondition(): KeywordCondition {
  return {
    sectionCategory: KeywordSectionCategory.CurrentWorkExperience,
    conditionType: CriteriaConditionType.IncludeAny,
    terms: [],
  }
}

/**
 * The CONTACT form's empty condition (`client-contact-search.page.component.ng1.ts:1385`).
 *
 * ⚠️ A DIFFERENT CATEGORY, AND REUSING THE CANDIDATE ONE IS A WIRE BUG. The contact field
 * offers two options, `0` and `1`; the candidate helper defaults to `3`, which that dropdown
 * cannot even display. Seeding a contact condition with it leaves the select showing "Current
 * experience" while `mapKeywords` sends `3` — a value the server reads as a different section
 * entirely, from a form that looks untouched.
 */
export function emptyClientContactKeywordCondition(): KeywordCondition {
  return {
    sectionCategory: ClientContactKeywordSectionCategory.CurrentWorkExperience,
    conditionType: CriteriaConditionType.IncludeAny,
    terms: [],
  }
}

export function loadFieldOfExpertise(
  saved: SavedCriteria['fieldOfExpertise'],
  otherFieldsFieldName?: string,
  extendedTitleClassEnabled?: boolean,
): FieldOfExpertiseRow[] {
  // ⚠️ `length > 0`, so an empty array seeds a row. The skills loader below does not.
  if (saved === undefined || saved === null || saved.length === 0) {
    return [emptyFieldOfExpertiseRow(otherFieldsFieldName, extendedTitleClassEnabled)]
  }
  return saved.map((row) => ({
    ...(row.field === undefined ? {} : { field: row.field }),
    ...(row.position === undefined ? {} : { position: row.position }),
    // Plain strings on the wire, tags in the form — as skills and companies are.
    ...(row.otherPositions == null ? {} : { otherPositions: row.otherPositions.map((text) => ({ text })) }),
    minYears: row.yoe?.min ?? null,
    maxYears: row.yoe?.max ?? null,
    ...(row.when === undefined ? {} : { when: row.when as LastUsedPeriod }),
  }))
}

export function loadSkills(saved: SavedCriteria['skills']): SkillCriteriaRow[] {
  // ⚠️ `!!skillCriteria` ONLY. An empty array comes back empty — no seeded row — which is the
  // one place these two sections disagree about what "nothing saved" looks like.
  if (saved === undefined || saved === null) return [emptySkillRow()]
  return saved.map((row) => ({
    skills: (row.skills ?? []).map((text) => ({ text })),
    minYears: row.yearsRange?.min ?? null,
    maxYears: row.yearsRange?.max ?? null,
    ...(row.lastUsed === undefined ? {} : { lastUsed: row.lastUsed as LastUsedPeriod }),
    ...(row.allowUnknownYears === undefined ? {} : { allowUnknownYears: row.allowUnknownYears }),
  }))
}

/**
 * ⚠️ THE EMPTY CONDITION IS A PARAMETER BECAUSE THE TWO FORMS DISAGREE ON IT. The candidate
 * default category is the enum's last member (`3`); the contact field offers only `0` and `1`,
 * so seeding a contact form from the candidate helper leaves the select reading "Current
 * experience" while `mapKeywords` sends `3` — on a blank form nobody has touched, which is the
 * commonest path of all.
 */
export function loadKeywords(
  saved: SavedCriteria['keywords'],
  empty: () => KeywordCondition = emptyKeywordCondition,
): KeywordCondition[] {
  if (saved === undefined || saved === null || saved.conditions === undefined || saved.conditions === null) {
    return [empty()]
  }
  return saved.conditions.map((condition) => ({
    ...(condition.sectionCategory === undefined
      ? {}
      : { sectionCategory: condition.sectionCategory as KeywordSectionCategory }),
    ...(condition.conditionType === undefined
      ? {}
      : { conditionType: condition.conditionType as CriteriaConditionType }),
    terms: (condition.terms ?? []).map((text) => ({ text })),
  }))
}

/**
 * The six work-experience blocks, which the wire keeps as six SEPARATE top-level keys.
 *
 * ⚠️ THREE OF THEM DERIVE `important` FROM PRESENCE. `companyTiers`, `qualityCompanyOnly` and
 * `candidateTurnover` carry no such flag on the wire — the criterion existing IS the ticked
 * checkbox, and the server only sends the period. Copying the object across leaves every one of
 * those three boxes unticked on a saved alert that uses them.
 */
export function loadWorkExperience(saved: SavedCriteria): WorkExperienceCriteria {
  const anyTime = LastUsedPeriod.AnyTime

  return {
    industries:
      saved.industries?.industries == null
        ? { period: anyTime }
        : {
            industries: saved.industries.industries.map((industry) => ({ text: industry.text })),
            ...(saved.industries.period === undefined ? {} : { period: saved.industries.period as LastUsedPeriod }),
          },
    companies:
      saved.companies?.companies == null
        ? { period: anyTime }
        : {
            // ⚠️ STRINGS on the wire here, OBJECTS for industries. Two lists, two shapes.
            companies: saved.companies.companies.map((text) => ({ text })),
            ...(saved.companies.period === undefined ? {} : { period: saved.companies.period as LastUsedPeriod }),
          },
    companyTypes:
      saved.companyTypes == null
        ? { period: anyTime }
        : { ...saved.companyTypes, period: saved.companyTypes.period as LastUsedPeriod },
    companyTiers:
      saved.companyTiers == null
        ? { period: anyTime }
        : { important: true, period: (saved.companyTiers.period ?? anyTime) as LastUsedPeriod },
    qualityCompanyOnly:
      saved.qualityCompanyOnly == null
        ? { period: anyTime }
        : { important: true, period: (saved.qualityCompanyOnly.period ?? anyTime) as LastUsedPeriod },
    candidateTurnover:
      saved.candidateTurnover == null
        ? // ⚠️ RATE 1 AND `EXCLUDE`, not empty — and Exclude is the first of the two options,
          // which is why the select shows it before anything is chosen.
          { minTurnoverRate: 1, condition: CriteriaConditionType.Exclude }
        : {
            important: true,
            ...(saved.candidateTurnover.minTurnoverRate === undefined
              ? {}
              : { minTurnoverRate: saved.candidateTurnover.minTurnoverRate }),
            // Only when non-null — a null leaves the default in place rather than clearing it.
            condition: (saved.candidateTurnover.condition ?? CriteriaConditionType.Exclude) as CriteriaConditionType,
          },
  }
}

/**
 * `initEducationSection` — folds the wire's per-VALUE flags into the form's per-GROUP ones.
 *
 * ⚠️ A GROUP IS TICKED WHEN **ANY** OF ITS VALUES IS. `groups.forEach(g => values[g.key] =
 * g.values.some(...))`, so a group is on when one member is saved, not when all are. Reading it
 * as "all" leaves partially-saved groups unticked.
 */
export function loadEducationSection(
  section: Record<string, boolean> | null | undefined,
  groups: readonly EducationGroup[],
): Record<string, boolean> {
  if (section === undefined || section === null) return {}
  const folded: Record<string, boolean> = {}
  for (const group of groups) folded[group.key] = (group.values ?? []).some((value) => section[value] === true)
  return folded
}

export function loadEducation(
  saved: SavedCriteria['education'],
  degreeGroups: readonly EducationGroup[] = [],
  fieldGroups: readonly EducationGroup[] = [],
): EducationCriteria {
  if (saved === undefined || saved === null) return {}
  // `Object.assign({}, education, {…})` — the raw criteria first, then the three folded
  // sections OVER it, so anything else the DTO carries survives untouched.
  return {
    ...saved,
    degreeType: loadEducationSection(saved.degreeType, degreeGroups),
    fieldCategory: loadEducationSection(saved.fieldCategory, fieldGroups),
    instituteBadge: saved.instituteBadge ?? {},
  } as EducationCriteria
}

export function loadContactDetails(saved: SavedCriteria['contactDetails']): ContactDetailsCriteria {
  // ⚠️ AN OBJECT WITH BOTH FLAGS FALSE IS "NOTHING SAVED". Upstream returns `{}` unless at least
  // one is set, and then copies across only the ones that are TRUE — so a saved `false` never
  // reaches the form as a key.
  if (saved === undefined || saved === null) return {}
  if (saved.hasEmailOnly !== true && saved.hasPhoneOnly !== true) return {}
  return {
    ...(saved.hasEmailOnly === true ? { hasEmailOnly: true } : {}),
    ...(saved.hasPhoneOnly === true ? { hasPhoneOnly: true } : {}),
  }
}

export function loadLocation(saved: SavedCriteria['location']): LocationCriteria {
  if (saved === undefined || saved === null) return {}

  const loaded: LocationCriteria = {
    ...(saved.country === undefined ? {} : { country: saved.country }),
    regions: {},
    // ⚠️ 10 UNCONDITIONALLY, BEFORE the saved distance is consulted. A location criterion with
    // no distance still arrives with a radius of 10 selected, which is what the select shows.
    distance: 10,
    ...(saved.allowUnknown === undefined ? {} : { allowUnknown: saved.allowUnknown }),
  }

  // The wire's array of `{ region_state }` becomes the form's `Record<name, true>`.
  for (const region of saved.regions ?? []) loaded.regions = { ...loaded.regions, [region.region_state]: true }

  // ⚠️ EACH LIST SETS ITS OWN `include*` FLAG FROM BEING NON-EMPTY. The checkbox is derived, not
  // stored — the same rule the section applies when a tag is added.
  // ⚠️ THE WHOLE WIRE OBJECT IS KEPT, NOT JUST ITS LABEL. `IExtensionLocation` carries
  // `id`, `city`, `region_state`, `country`, `lat` and `lon`, and `mapLocation` sends them
  // back — a tag reduced to `{ text }` round-trips a resolved city into a bare string the
  // server cannot place. Measured: the save 400s before it gets that far, but the loss would
  // have outlived the fix for it.
  const cities = saved.cities ?? []
  if (cities.length > 0) {
    loaded.cities = cities.map((city) => ({ text: city.text ?? city.city ?? '', source: { ...city } }))
    loaded.includeCities = true
  }

  const global = saved.globalCitiesOrRegions ?? []
  if (global.length > 0) {
    loaded.globalCitiesOrRegions = global.map((entry) => ({
      text: entry.text ?? entry.id ?? '',
      source: { ...entry },
    }))
    loaded.includeGlobalCitiesOrRegions = true
  }

  const distances = saved.distances ?? []
  if (distances.length > 0) {
    loaded.distanceCities = distances.map((entry) => ({ text: entry.text ?? entry.city ?? '', source: { ...entry } }))
    // The radius is the FIRST entry's, not a separate field — every city shares one radius.
    loaded.distance = distances[0]?.distance ?? 10
    loaded.includeDistanceFrom = true
  }

  return loaded
}

/** `initAtsFilters` — the saved rows, or ONE empty row. */
export function loadAtsFilters(saved: SavedCriteria['atsFilters']): AtsFilterRow[] {
  if (saved === undefined || saved === null || saved.length === 0) return [{}]
  return saved.map((filter) => ({
    ...(filter.section === undefined ? {} : { section: filter.section }),
    // `Object.assign({}, filter.data)` — a copy, so editing a row cannot mutate the alert.
    ...(filter.data === undefined ? {} : { data: { ...(filter.data as object) } as AtsFilterRow['data'] }),
  }))
}

/**
 * `initExternalSourceSearchCriteriaFiltersCriteria` — and its guard is three conditions deep.
 *
 * ⚠️ A MAP WHOSE EVERY LIST IS EMPTY COUNTS AS NOTHING SAVED. Upstream requires the object to
 * exist, to have keys, AND for at least one of those keys to hold a non-empty list; otherwise it
 * returns `{}` and the per-source seeding puts one empty row under each configured source.
 *
 * ⚠️ AND DATES ARRIVE AS STRINGS. The server serialises `from`/`to` as ISO text and upstream
 * patches them back into `Date` objects (a TODO-flagged block, RND-6877) — without that the
 * date inputs get a string where they expect a Date and render nothing.
 */
export function loadExternalSourceFilters(
  saved: SavedCriteria['externalSourceSearchCriteriaFilters'],
): Record<string, ExternalSourceFilterRow[]> {
  if (saved === undefined || saved === null) return {}
  const hosts = Object.keys(saved)
  if (hosts.length === 0) return {}
  if (!hosts.some((host) => (saved[host] ?? []).length > 0)) return {}

  const loaded: Record<string, ExternalSourceFilterRow[]> = {}
  for (const host of hosts) {
    loaded[host] = (saved[host] ?? []).map((filter) => {
      const value = filter.value
      if (value !== undefined && value.type === 'DATE') {
        return {
          ...filter,
          value: {
            type: 'DATE',
            from: value.from == null ? null : new Date(value.from as unknown as string),
            to: value.to == null ? null : new Date(value.to as unknown as string),
          },
        }
      }
      return { ...filter, ...(value === undefined ? {} : { value: { ...value } }) }
    })
  }
  return loaded
}
